import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, HelpCircle, Bell, Trophy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${window.location.origin}/api`;
const LOGO_URL = "/blackified-logo.png";

export const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          setUser(null);
          setUnreadCount(0);
        }
      } catch (error) {
        console.error('Auth check error:', error);
      }
    };

    checkAuth();
  }, [location.pathname]);

  // Fetch unread notifications count when user is authenticated
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await fetch(`${API}/notifications?limit=1`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setUnreadCount(data.unread_count || 0);
        }
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };

    if (user) {
      fetchUnreadCount();
    }
  }, [user]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/businesses', label: 'Directory' },
    { href: '/blog', label: 'Blog' },
    { href: '/bounties', label: 'Bounties' },
    { href: '/leaderboard', label: 'Leaderboard', icon: Trophy },
    { href: '/submit-business', label: 'List Your Business' },
    { href: '/help', label: 'Help', icon: HelpCircle },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-[#050505]/90 backdrop-blur-lg border-b border-zinc-900' 
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="shrink-0" data-testid="navbar-logo">
            <img 
              src={LOGO_URL} 
              alt="Blackified" 
              className="h-6 md:h-8 object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="nav-link"
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <div className="flex items-center gap-3">
                {/* Notifications Bell */}
                <Link
                  to="/notifications"
                  className="relative p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
                  data-testid="nav-notifications"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-medium">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="nav-link text-purple-400 hover:text-purple-300"
                    data-testid="nav-admin"
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-colors"
                  data-testid="nav-dashboard"
                >
                  {user.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="w-6 h-6 rounded-full"
                    />
                  ) : (
                    <User className="w-4 h-4 text-zinc-400" />
                  )}
                  <span className="text-sm text-zinc-300">{user.name?.split(' ')[0]}</span>
                </Link>
              </div>
            ) : (
              <Link
                to="/auth"
                className="btn-primary text-sm py-2 px-6"
                data-testid="nav-signin"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-zinc-400 hover:text-white"
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed inset-0 z-40 bg-[#050505] pt-16 md:hidden"
          >
            <div className="p-6 space-y-6">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className="block text-xl text-zinc-300 hover:text-white"
                >
                  {link.label}
                </Link>
              ))}

              <div className="pt-6 border-t border-zinc-900">
                {user ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                          <User className="w-5 h-5 text-zinc-400" />
                        </div>
                      )}
                      <div>
                        <p className="text-white font-medium">{user.name}</p>
                        <p className="text-zinc-500 text-sm">{user.email}</p>
                      </div>
                    </div>
                    
                    <Link
                      to="/dashboard"
                      onClick={() => setIsOpen(false)}
                      className="block btn-secondary text-center"
                    >
                      Dashboard
                    </Link>

                    <Link
                      to="/notifications"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center justify-center gap-2 text-zinc-300 hover:text-white"
                    >
                      <Bell className="w-5 h-5" />
                      Notifications
                      {unreadCount > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-blue-500 text-white text-xs">
                          {unreadCount}
                        </span>
                      )}
                    </Link>

                    {user.role === 'admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setIsOpen(false)}
                        className="block text-center text-purple-400"
                      >
                        Admin Panel
                      </Link>
                    )}
                  </div>
                ) : (
                  <Link
                    to="/auth"
                    onClick={() => setIsOpen(false)}
                    className="block btn-primary text-center"
                  >
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
